import type { Filter } from "@orsane/schema/filters";
import type { TaskListType } from "@orsane/schema/task-lists";
import { useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTaskListPreferences } from "@/queries/list-queries";
import type { TaskQueryParams } from "@/types/list";

/**
 * Resolves TaskQueryParams from route search params and task list preferences.
 * Use this in route components to provide values to TaskQueryParamsProvider.
 *
 * Memoized because the result goes straight into TaskQueryParamsProvider: a fresh
 * object on every route render re-renders every consumer of that context, and
 * those consumers include each row's title cell and every cell's mutation hook.
 */
export function useRouteTaskQueryParams({
    taskListId,
    type,
}: {
    taskListId: number | undefined;
    type: TaskListType;
}): TaskQueryParams {
    const filter = useSearch({ strict: false, select: (s: any) => s.filter as Filter[] | undefined }) as
        | Filter[]
        | undefined;
    const { preferences } = useTaskListPreferences({ taskListId, type });
    const completedTasks = preferences?.["taskList.completedTasks"];

    return useMemo(() => ({ type, taskListId, filter, completedTasks }), [type, taskListId, filter, completedTasks]);
}
